// ── In-App Purchase (RevenueCat) — NATIVE iOS ONLY ──────────────────────────
//
// Apple Guideline 3.1.1 requires digital goods consumed in-app to be sold via
// Apple IAP. On iOS, Elite is purchased through RevenueCat (which wraps StoreKit);
// on web, Elite stays pure-Stripe (see EliteUpgradeModal). A coach who subscribed
// on web reads through on iOS with NO Apple cut — the entitlement is OR'd in, the
// IAP purchase path is only for coaches who choose to subscribe inside the app.
//
// Every function here is a safe no-op on web (guarded by Capacitor.isNativePlatform()).
// Real product IDs / offering live in the RevenueCat dashboard, not in code.

import { Capacitor } from '@capacitor/core';
import {
  Purchases,
  LOG_LEVEL,
  INTRO_ELIGIBILITY_STATUS,
  PACKAGE_TYPE,
  type PurchasesPackage,
  type CustomerInfo,
} from '@revenuecat/purchases-capacitor';

// ── Config — fill in from the RevenueCat dashboard ──────────────────────────
// Apple-platform PUBLIC SDK key (starts with "appl_"). This is a publishable
// key, safe to ship in the client bundle.
const RC_APPLE_PUBLIC_KEY = 'appl_WwIYgDDFFwDsDcuKSwAdTDtXRZJ';

// Entitlement identifier configured in RevenueCat. When this entitlement is
// active, the coach has Elite. ⚠️ Must match the RevenueCat dashboard EXACTLY
// (case- and space-sensitive). The dashboard identifier is "Dugout Elite".
export const ELITE_ENTITLEMENT_ID = 'Dugout Elite';

let configured = false;

function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Configure RevenueCat and associate the purchase with the Firebase user.
 * Call once after auth resolves with the Firebase UID. No-op on web.
 * Safe to call repeatedly — only configures once, but always re-logs-in the uid.
 */
export async function initIAP(uid: string): Promise<void> {
  if (!isNative() || !uid) return;
  try {
    if (!configured) {
      await Purchases.setLogLevel({ level: LOG_LEVEL.WARN });
      // appUserID ties this device's purchases to the Firebase UID so the
      // entitlement follows the coach across devices / reinstalls.
      await Purchases.configure({ apiKey: RC_APPLE_PUBLIC_KEY, appUserID: uid });
      configured = true;
    } else {
      await Purchases.logIn({ appUserID: uid });
    }
  } catch (e) {
    console.error('[iap] initIAP failed', e);
  }
}

/** True if the Elite entitlement is active in this CustomerInfo. */
export function hasEliteEntitlement(info: CustomerInfo | null | undefined): boolean {
  if (!info) return false;
  return !!info.entitlements.active[ELITE_ENTITLEMENT_ID];
}

/** Read the current entitlement state once. No-op → false on web. */
export async function isIapElite(): Promise<boolean> {
  if (!isNative()) return false;
  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    return hasEliteEntitlement(customerInfo);
  } catch (e) {
    console.error('[iap] isIapElite failed', e);
    return false;
  }
}

/**
 * Subscribe to live entitlement updates (purchase, renewal, restore, expiry).
 * Returns an unsubscribe function. No-op on web (returns a noop unsub).
 */
export function addEliteListener(cb: (isElite: boolean) => void): () => void {
  if (!isNative()) return () => {};
  const handler = (info: CustomerInfo) => cb(hasEliteEntitlement(info));
  // addCustomerInfoUpdateListener resolves to a callback id used to remove it.
  const idPromise = Purchases.addCustomerInfoUpdateListener(handler);
  return () => {
    idPromise
      .then((listenerToRemove) =>
        Purchases.removeCustomerInfoUpdateListener({ listenerToRemove })
      )
      .catch((e) => console.error('[iap] removeCustomerInfoUpdateListener failed', e));
  };
}

/** The two Elite packages (monthly / annual) from the current offering. */
export interface ElitePackages {
  monthly: PurchasesPackage | null;
  annual: PurchasesPackage | null;
}

/**
 * Fetch the current offering's monthly + annual packages with display prices.
 * Returns nulls on web or if the offering isn't configured yet.
 */
export async function getElitePackages(): Promise<ElitePackages> {
  if (!isNative()) return { monthly: null, annual: null };
  try {
    const { current } = await Purchases.getOfferings();
    if (!current) return { monthly: null, annual: null };
    return {
      monthly: current.monthly ?? null,
      annual: current.annual ?? null,
    };
  } catch (e) {
    console.error('[iap] getElitePackages failed', e);
    return { monthly: null, annual: null };
  }
}

/**
 * Purchase a package. Resolves to { ok, isElite } — ok=false on user cancel,
 * isElite reflects the entitlement after the purchase settles.
 */
export async function purchaseElite(
  pkg: PurchasesPackage
): Promise<{ ok: boolean; isElite: boolean; error?: string }> {
  if (!isNative()) return { ok: false, isElite: false };
  try {
    const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg });
    return { ok: true, isElite: hasEliteEntitlement(customerInfo) };
  } catch (e: any) {
    // RevenueCat sets userCancelled on the error when the coach backs out of
    // the StoreKit sheet — that's not an error worth surfacing.
    if (e?.userCancelled || e?.code === '1' /* PURCHASE_CANCELLED */) {
      return { ok: false, isElite: false };
    }
    console.error('[iap] purchaseElite failed', e);
    return { ok: false, isElite: false, error: e?.message ?? 'Purchase failed.' };
  }
}

/**
 * Restore Purchases — Apple-required for non-consumable / subscription apps.
 * Resolves to the entitlement state after restore.
 */
export async function restorePurchases(): Promise<{ isElite: boolean; error?: string }> {
  if (!isNative()) return { isElite: false };
  try {
    const { customerInfo } = await Purchases.restorePurchases();
    return { isElite: hasEliteEntitlement(customerInfo) };
  } catch (e: any) {
    console.error('[iap] restorePurchases failed', e);
    return { isElite: false, error: e?.message ?? 'Could not restore purchases.' };
  }
}

// ── Trial terms — Apple Guideline 3.1.2(c) ──────────────────────────────────
//
// The paywall must state the trial length AND the exact amount billed after it,
// sourced from the store rather than hardcoded, so the disclosure can never
// drift from what Apple actually charges.
//
// Eligibility matters as much as the offer: Apple returns introPrice on the
// product whether or not THIS Apple ID can still use it. A coach who already
// burned the trial is charged immediately, so showing "14 days free" to them
// would misstate the terms in the other direction. We only promise a trial when
// RevenueCat reports the account as eligible.

export interface TrialTerms {
  /** e.g. "14 days" — omitted when the coach isn't eligible for the trial. */
  duration: string | null;
  /** Full renewal charge + cadence, e.g. "$59.99/year". Always present. */
  renewalPrice: string;
}

/** "DAY" + 14 → "14 days"; "MONTH" + 1 → "1 month". */
function formatPeriod(unit: string, count: number): string {
  const noun = unit.toLowerCase();
  return `${count} ${count === 1 ? noun : `${noun}s`}`;
}

/**
 * The cadence word shown after the renewal price. subscriptionPeriod is ISO 8601
 * ("P1Y" / "P1M") but is nullable, so fall back to the RevenueCat package type
 * rather than defaulting a yearly plan to "month" and misstating the terms.
 */
function cadenceFor(pkg: PurchasesPackage): string {
  const period = pkg.product.subscriptionPeriod;
  if (period) return period.endsWith('Y') ? 'year' : 'month';
  return pkg.packageType === PACKAGE_TYPE.ANNUAL ? 'year' : 'month';
}

/**
 * Build the Apple-compliant trial disclosure for a package, from live store
 * data. `duration` is null when there's no intro offer or the coach has already
 * used it — callers must then drop all trial language and sell at full price.
 */
export async function getTrialTerms(pkg: PurchasesPackage): Promise<TrialTerms> {
  const renewalPrice = `${pkg.product.priceString}/${cadenceFor(pkg)}`;
  const intro = pkg.product.introPrice;
  if (!isNative() || !intro || intro.periodNumberOfUnits <= 0) {
    return { duration: null, renewalPrice };
  }
  try {
    const eligibility = await Purchases.checkTrialOrIntroductoryPriceEligibility({
      productIdentifiers: [pkg.product.identifier],
    });
    const status = eligibility[pkg.product.identifier]?.status;
    // Only ELIGIBLE promises a trial. UNKNOWN is deliberately treated as
    // ineligible: quoting full price and then granting a free trial is a
    // pleasant surprise, the reverse is a rejection.
    if (status !== INTRO_ELIGIBILITY_STATUS.INTRO_ELIGIBILITY_STATUS_ELIGIBLE) {
      return { duration: null, renewalPrice };
    }
  } catch (e) {
    console.error('[iap] trial eligibility check failed', e);
    return { duration: null, renewalPrice };
  }
  return {
    duration: formatPeriod(intro.periodUnit, intro.periodNumberOfUnits),
    renewalPrice,
  };
}

/**
 * Open the iOS system subscription-management screen (App Store → Subscriptions).
 * RevenueCat surfaces the managementURL; fall back to the App Store deep link.
 */
export async function openManageSubscriptions(): Promise<void> {
  if (!isNative()) return;
  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    const url = customerInfo.managementURL;
    if (url) {
      window.open(url, '_blank');
    } else {
      window.open('itms-apps://apps.apple.com/account/subscriptions', '_blank');
    }
  } catch (e) {
    console.error('[iap] openManageSubscriptions failed', e);
    window.open('itms-apps://apps.apple.com/account/subscriptions', '_blank');
  }
}
